import React from 'react';
import { Button } from '@brighterion/br-ui';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { useTranslation } from 'react-i18next';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useLocation, useHistory } from 'react-router-dom';
import ModalTitleFileNameAndSize from 'Components/ModalTitleFileNameAndSize';
import { noop } from 'lodash';
import appStyles from 'Routes/Routes.css';
import useIsMounted from 'Hooks/useIsMounted';

import styles from './Cancelation.css';
import { POSSIBLE_STATUS, updateStatus, useUploaderContext, useUploaderDispatchContext } from '../../Uploader/context';

const Cancelation = () => {
    // react-router-dom provides a way to "block" history changes
    // use a ref to keep track of the blocker and unblocker function.
    const historyBlockRef = React.useRef<() => void>(noop);

    const { fileDetails, currentChunkNumber, status } = useUploaderContext();
    const dispatch = useUploaderDispatchContext();
    const totalRows = Math.ceil(fileDetails.totalNumberOfRows / fileDetails.totalChunks) * currentChunkNumber;
    const { t } = useTranslation();
    const { addModal, clearAll } = useReduxModal();
    const location = useLocation();
    const history = useHistory();
    const isMounted = useIsMounted();

    const onCancel = React.useCallback(() => {
        // early returns
        if (!isMounted) return;

        addModal({
            id: 'bulk-upload-cancel',
            status: 'warning',
            title: 'Cancel Bulk Upload',
            content: (
                <div className={styles.cancelModalContent}>
                    <p>{t('singleListBulkUpload.areYouSure')}</p>
                    <p>{t('singleListBulkUpload.alreadyUploaded')}</p>
                </div>
            ),
            cancelText: t('Resume'),
            onActionText: t('Cancel Bulk Upload'),
            onAction: () => {
                dispatch(updateStatus(POSSIBLE_STATUS.canceled));
                historyBlockRef?.current();
                clearAll();
                const routeToGoTo = location.pathname.split('/').slice(0, 3).join('/');
                history.push(routeToGoTo);

                addModal({
                    id: 'bulk-upload-error-cancel',
                    status: 'error',
                    title: 'Bulk Upload Canceled',
                    cancelText: t('Close'),
                    content: (
                        <div className={styles.errorCancelModalContent}>
                            <p>
                                {t('Bulk upload has been canceled')} <br />
                                <span className={appStyles.bold}>
                                    {t('singleListBulkUpload.cancelRowCount', { rowCount: totalRows })}
                                </span>
                            </p>
                            <p>{t('singleListBulkUpload.processInterrupted')}</p>
                            <p>{t('singleListBulkUpload.cancelCloseLastPara')}</p>
                        </div>
                    ),
                    titleChildren: (
                        <ModalTitleFileNameAndSize fileName={fileDetails.fileName} fileSize={fileDetails.fileSize} />
                    )
                });
            }
        });
    }, [
        addModal,
        clearAll,
        dispatch,
        fileDetails.fileName,
        fileDetails.fileSize,
        history,
        location.pathname,
        t,
        totalRows,
        isMounted
    ]);

    React.useEffect(() => {
        historyBlockRef.current = history.block(() => {
            // Note: status is updated every time a chunk is processed
            if (status === POSSIBLE_STATUS.chunking) {
                historyBlockRef?.current();
                onCancel();
                return false;
            }

            return false;
        });

        return () => {
            if (historyBlockRef?.current) {
                historyBlockRef.current();
            }
        };
    }, [history, onCancel, status]);

    return (
        <div className={styles.cancelContainer}>
            <Button
                dataTestId='cancel-bulk-upload'
                id='cancel-bulk-upload'
                variant='contextual-secondary'
                onClick={handleOnClickEvent(onCancel)}
                onKeyDown={handleKeyEvent(onCancel)}
            >
                {t('Cancel Bulk Upload')}
            </Button>
        </div>
    );
};

export default Cancelation;
